import React, { useContext, useState } from "react";
import { Form, Collapse, Card } from "react-bootstrap";
import { useSyncedField } from "../../components/Sidebar";
import SidebarContext from "../../components/Sidebar/SidebarContext";
import WebhookAuth from "./WebhookAuth";
import WebhookHeaders from "./WebhookHeaders";
import WebhookTestModal from "./WebhookTestModal";

const HTTP_METHODS = ["POST", "GET", "PUT", "PATCH", "DELETE"];

const CollapsibleSection = ({ title, children }) => {
    const [open, setOpen] = useState(false);
    return (
        <Card className="mb-3 border">
            <Card.Header
                onClick={() => setOpen((o) => !o)}
                className="d-flex align-items-center justify-content-between py-2 px-3"
                style={{ cursor: "pointer", userSelect: "none", background: "transparent" }}
            >
                <span className="fw-semibold small text-uppercase text-muted">{title}</span>
                <i
                    className={`fa fa-chevron-${open ? "up" : "down"} text-muted`}
                    style={{ fontSize: "0.7rem" }}
                />
            </Card.Header>
            <Collapse in={open}>
                <div>
                    <Card.Body className="p-0">{children}</Card.Body>
                </div>
            </Collapse>
        </Card>
    );
};

const WebhookBasicDetails = () => {
    const { emittedData, onSave } = useContext(SidebarContext);

    const [name, setName] = useSyncedField("name", "");
    const [description, setDescription] = useSyncedField("description", "");
    const [url, setUrl] = useSyncedField("url", "");
    const [method, setMethod] = useSyncedField("method", "POST");
    const [is_active, setIsActive] = useSyncedField("is_active", true);
    const [notification_email, setNotificationEmail] = useSyncedField("notification_email", "");
    const [parameters, setParameters] = useSyncedField("parameters", {});

    const [showTest, setShowTest] = useState(false);

    const apiType = parameters?.api_type ?? "REST";
    const isGraphQL = apiType === "GRAPHQL";

    const setApiType = (type) => {
        setParameters({ ...parameters, api_type: type });
        // GraphQL always uses POST; reset method when switching to REST
        if (type === "GRAPHQL") setMethod("POST");
    };

    const updateGraphql = (patch) => setParameters({ ...parameters, ...patch });

    const canTest = isGraphQL
        ? !!(name?.trim() && url?.trim() && parameters?.graphql_query?.trim())
        : !!(name?.trim() && url?.trim());

    const testButtonTitle = isGraphQL
        ? (canTest ? "Send a test GraphQL request" : "Fill in Name, Endpoint URL, and Query first")
        : (canTest ? "Send a test REST request to this URL" : "Fill in Name and URL first");

    return (
        <div className="p-3">
            <Form>
                <Form.Group className="mb-3">
                    <Form.Label>
                        Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="My webhook"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={description ?? ""}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="What does this webhook do?"
                    />
                </Form.Group>

                {/* API type toggle */}
                <Form.Group className="mb-3">
                    <Form.Label>API Type</Form.Label>
                    <div
                        className="btn-group w-100"
                        role="group"
                        aria-label="API type"
                    >
                        <button
                            type="button"
                            className={`btn btn-sm ${!isGraphQL ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setApiType("REST")}
                        >
                            <i className="fa fa-exchange me-1" />REST
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm ${isGraphQL ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setApiType("GRAPHQL")}
                        >
                            <i className="fa fa-code me-1" />GraphQL
                        </button>
                    </div>
                </Form.Group>

                {/* REST fields */}
                {!isGraphQL && (
                    <Form.Group className="mb-3">
                        <Form.Label>
                            URL <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Select
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                                style={{ width: 110, flexShrink: 0 }}
                            >
                                {HTTP_METHODS.map((m) => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </Form.Select>
                            <Form.Control
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://example.com/hook"
                            />
                        </div>
                    </Form.Group>
                )}

                {/* GraphQL fields */}
                {isGraphQL && (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Endpoint URL <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://example.com/graphql"
                            />
                            <Form.Text className="text-muted">
                                Always sent as POST with <code>application/json</code>.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Query / Mutation <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                value={parameters?.graphql_query ?? ""}
                                onChange={(e) => updateGraphql({ graphql_query: e.target.value })}
                                placeholder={`mutation NotifyExternalSystem($input: WebhookInput!) {\n  notify(input: $input) {\n    success\n  }\n}`}
                                style={{ fontFamily: "monospace", fontSize: "0.82rem" }}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Operation Name</Form.Label>
                            <Form.Control
                                value={parameters?.graphql_operation_name ?? ""}
                                onChange={(e) => updateGraphql({ graphql_operation_name: e.target.value })}
                                placeholder="NotifyExternalSystem"
                            />
                            <Form.Text className="text-muted">
                                Required only when the query document contains multiple operations.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Variables</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                value={parameters?.graphql_variables ?? ""}
                                onChange={(e) => updateGraphql({ graphql_variables: e.target.value })}
                                placeholder={`{\n  "input": { "source": "starek" }\n}`}
                                style={{ fontFamily: "monospace", fontSize: "0.82rem" }}
                            />
                            <Form.Text className="text-muted">
                                JSON object. Static values only — sent as-is with every invocation.
                            </Form.Text>
                        </Form.Group>
                    </>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Failure Notification Email</Form.Label>
                    <Form.Control
                        type="email"
                        value={notification_email ?? ""}
                        onChange={(e) => setNotificationEmail(e.target.value)}
                        placeholder="ops@example.com"
                    />
                    <Form.Text className="text-muted">
                        Receives an email when this webhook returns a non-2xx response.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Check
                        type="switch"
                        id="webhook-active-switch"
                        label="Active"
                        checked={!!is_active}
                        onChange={(e) => setIsActive(e.target.checked)}
                    />
                </Form.Group>
            </Form>

            <CollapsibleSection title="Authentication">
                <WebhookAuth />
            </CollapsibleSection>

            <CollapsibleSection title="Custom Headers">
                <WebhookHeaders />
            </CollapsibleSection>

            <div className="d-flex justify-content-end mt-1">
                <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={!canTest}
                    title={testButtonTitle}
                    onClick={() => setShowTest(true)}
                >
                    <i className="fa fa-bolt me-1" />Test Connection
                </button>
            </div>

            <WebhookTestModal
                show={showTest}
                onHide={() => setShowTest(false)}
                onConnect={onSave}
                webhookData={emittedData}
            />
        </div>
    );
};

export default WebhookBasicDetails;
